'use client';

import { useCallback } from 'react';

import { usePlatform } from '@/components/providers/platform-provider';
import { useTrackEvent } from '@/hooks/use-track-event';
import { isRepeatOfRecentClick } from '@/lib/analytics/link-click-dedup';
import type { TrackedPlatform } from '@/lib/analytics/tracked-platform';
import {
  CHAT_ANALYTICS_EVENTS,
  WIDGET_ANALYTICS_EVENTS,
  type AbortReason,
  type ChatAnalyticsEvent,
  type WidgetAnalyticsEvent,
} from '@/lib/constants/post-message-types';
import { useSyncStore } from '@/lib/sync/store';

export type LinkClickSource =
  | 'text'
  | 'carousel'
  | 'flight-card'
  | 'vacation-package-card'
  | 'organized-trip-card'
  | 'reservation-card'
  | 'sidebar'
  | 'fund-card';

/**
 * The fields every `link_clicked` carries from both surfaces, so one query covers chat and the MCP
 * panel. `surface: 'widget'` is the MCP panel, not chat's `isWidget` embed.
 */
export type LinkClickedContract = {
  url: string;
  platform: TrackedPlatform;
  surface: 'chat' | 'widget';
  source: LinkClickSource;
  navigationMode: string;
};

export type LinkClickEventData = {
  url: string;
  linkText?: string;
  navigationMode: string;
  source: LinkClickSource;
  threadId?: string;
  /** The tool whose result rendered the link; absent outside a tool result. */
  toolName?: string;
};

export type WidgetEventProperties = {
  trigger?: string;
};

export type CarouselShownEventData = {
  products: string[];
  toolName: string;
};

export type CardAction = 'view_rooms' | 'select_room';
export type CardItemType = 'hotel' | 'room';

export type CardActionEventData = {
  action: CardAction;
  itemType: CardItemType;
  /** The supplier's own id, so an action ties back to what was on screen. */
  itemId?: string;
  source?: LinkClickEventData['source'];
  /** Which surface rendered it, so one funnel can still tell them apart. */
  surface: 'chat' | 'widget';
  toolName?: string;
  /**
   * Where this action sits in a card flow that takes more than one, 1-based, and how many it takes
   * in total. Nothing is emitted for giving up, because nothing happens when a guest does: a funnel
   * over these reads abandonment as the step that has no successor, and needs to know which step of
   * how many to do it. Not room-specific — any in-card flow with steps can carry them.
   */
  step?: number;
  stepCount?: number;
};

export type ChatEventProperties = {
  threadId?: string;
  messageCount?: number;
  streamingDurationMs?: number;
  requestDurationMs?: number;
  abortReason?: AbortReason;
  isDocumentHidden?: boolean;
  isNetworkOffline?: boolean;
  isPagePersisted?: boolean;
  hadStreamingStarted?: boolean;
  wasQueued?: boolean;
  eventTimestamp?: number;
  queuedDurationMs?: number;
};

export function useAnalytics() {
  const trackEvent = useTrackEvent();
  const activeThreadId = useSyncStore(state => state.threadId);
  const platform = usePlatform();

  const trackLinkClick = useCallback(
    (data: LinkClickEventData) => {
      const threadId = activeThreadId ?? data.threadId;
      // Every dimension the event is attributed by, so the memory can never be broader than the
      // reporting: which thread, which surface, which link, and which element of it — the text
      // tells a card-body click from its CTA, the source tells a carousel from the message above.
      const key = `${threadId ?? ''}|${data.source}|${data.url}|${data.linkText ?? ''}`;
      if (isRepeatOfRecentClick({ key, now: Date.now() })) return;

      const properties: LinkClickedContract &
        Pick<LinkClickEventData, 'linkText' | 'threadId' | 'toolName'> = {
        ...data,
        platform,
        surface: 'chat',
        ...(threadId ? { threadId } : {}),
      };
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.LINK_CLICKED,
        // Stamped here rather than by each caller: every route into this hook is chat UI, while an
        // MCP panel reports the same event through the beacon.
        properties,
      });
    },
    [trackEvent, activeThreadId, platform],
  );

  const trackWidgetEvent = useCallback(
    (event: WidgetAnalyticsEvent, properties?: WidgetEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackChatEvent = useCallback(
    (event: ChatAnalyticsEvent, properties?: ChatEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackThreadEvent = useCallback(
    function trackThreadEvent({
      event,
      properties,
      ...card
    }: {
      event: ChatAnalyticsEvent;
      properties: Record<string, unknown>;
      /** The thread of the card that fired the event. Passed, it wins even when it is undefined:
       *  the active thread lags a thread switch (see `useCardThreadId`). */
      threadId?: string;
    }) {
      const threadId = 'threadId' in card ? card.threadId : activeThreadId;
      trackEvent({
        event,
        properties: { ...properties, ...(threadId ? { threadId } : {}) },
      });
    },
    [trackEvent, activeThreadId],
  );

  const trackCardAction = useCallback(
    function trackCardAction(data: CardActionEventData) {
      trackThreadEvent({ event: CHAT_ANALYTICS_EVENTS.CARD_ACTION, properties: data });
    },
    [trackThreadEvent],
  );

  const trackCarouselShown = useCallback(
    (data: CarouselShownEventData) => {
      trackEvent({ event: CHAT_ANALYTICS_EVENTS.CAROUSEL_SHOWN, properties: data });
    },
    [trackEvent],
  );

  return {
    trackLinkClick,
    trackCardAction,
    trackWidgetEvent,
    trackChatEvent,
    trackCarouselShown,
    trackThreadEvent,
    WIDGET_ANALYTICS_EVENTS,
    CHAT_ANALYTICS_EVENTS,
  };
}
